import React, { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, unwrapObject } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DashboardSkeleton } from "@/components/ui/page-skeletons";
import { useToast } from "@/context/ToastContext";
import { SuperAdminDashboard, type SuperAdminSummary } from "./SuperAdminDashboard";
import { SalesHeadDashboard, type SalesHeadSummary } from "./SalesHeadDashboard";
import { SalesExecDashboard, type SalesExecSummary } from "./SalesExecDashboard";
import { MerchandiserDashboard, type MerchandiserSummary } from "./MerchandiserDashboard";

type Summary =
  | ({ role: "SUPER_ADMIN" } & SuperAdminSummary)
  | ({ role: "SALES_HEAD" } & SalesHeadSummary)
  | ({ role: "SALES_EXEC" } & SalesExecSummary)
  | ({ role: "MERCHANDISER" } & MerchandiserSummary);

const SUBTITLE: Record<Summary["role"], string> = {
  SUPER_ADMIN: "How the whole company is doing this month.",
  SALES_HEAD: "How your team is doing, and who needs help.",
  SALES_EXEC: "What to work on today.",
  MERCHANDISER: "Samples to send, samples with buyers, and what is waiting to be dispatched.",
};

export function DashboardPage() {
  const { user } = useAuth();
  const { error } = useToast();
  const [data, setData] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/Dashboard/Summary");
      setData(unwrapObject<Summary>(res));
    } catch (err: any) {
      error("Dashboard", err.message || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading && !data) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-black tracking-tight text-slate-900">
            Welcome back, {user?.FirstName || "there"}
          </h2>
          <p className="text-sm font-medium text-slate-600 mt-0.5">{data ? SUBTITLE[data.role] : ""}</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50 shadow-2xs">
          <RefreshCw className={loading ? "size-4 animate-spin" : "size-4"} />
          Refresh
        </Button>
      </div>

      {data?.role === "SUPER_ADMIN" && <SuperAdminDashboard data={data} />}
      {data?.role === "SALES_HEAD" && <SalesHeadDashboard data={data} />}
      {data?.role === "SALES_EXEC" && <SalesExecDashboard data={data} />}
      {data?.role === "MERCHANDISER" && <MerchandiserDashboard data={data} />}
    </div>
  );
}
